import { Injectable } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { DisplayValueDescriptor } from '@vendasta/uikit';
import { BehaviorSubject, Observable } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { alignLocalDateToUTC } from '../../../core/metrics.helper';
import {
  Aggregate,
  Dimension,
  FieldFilter_Operator,
  GroupBy,
  Operator,
  Order,
  OrderBy,
  OrderColumn,
  QueryMetricsRequest,
  QueryMetricsResponse,
  Type,
} from '../../../core/multi_location_analytics_sdk/_internal';
import { MultiLocationAnalyticsService } from '../../../core/multi_location_analytics_sdk/multi-location-analytics.service';
import { DEVICES } from '../../../core/pagespeed.interface';
import { Site, SiteService } from '../../../core/site';
import { StatsApiService } from '../../../core/stats';
import { PageSpeedScoreResult } from '../../../core/stats/stats.api.service';
import { ChartEntry, ChartSeries } from '../../metrics';

export interface SpeedScoreInfo {
  device: string;
  currentScore: DisplayValueDescriptor;
  previousScore: DisplayValueDescriptor;
  updateDate: Date;
  passedRules: number;
  opportunities: number;
  improveCTAKey: string;
  scoreHistory: ChartSeries[];
}

@Injectable()
export class SpeedScoreService {
  routeParams: Params;
  desktopSpeedScoreInfo$$: BehaviorSubject<SpeedScoreInfo> = new BehaviorSubject<SpeedScoreInfo>(undefined);
  desktopSpeedScoreInfo$: Observable<SpeedScoreInfo> = this.desktopSpeedScoreInfo$$.asObservable();
  mobileSpeedScoreInfo$$: BehaviorSubject<SpeedScoreInfo> = new BehaviorSubject<SpeedScoreInfo>(undefined);
  mobileSpeedScoreInfo$: Observable<SpeedScoreInfo> = this.mobileSpeedScoreInfo$$.asObservable();
  site$: Observable<Site>;

  loadingDesktopSpeedScore$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  loadingDesktopSpeedScore$: Observable<boolean> = this.loadingDesktopSpeedScore$$.asObservable();

  loadingMobileSpeedScore$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  loadingMobileSpeedScore$: Observable<boolean> = this.loadingMobileSpeedScore$$.asObservable();

  desktopRefreshing$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  desktopRefreshing$: Observable<boolean> = this.desktopRefreshing$$.asObservable();
  mobileRefreshing$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  mobileRefreshing$: Observable<boolean> = this.mobileRefreshing$$.asObservable();

  constructor(
    private route: ActivatedRoute,
    private multiLocationAnalyticsService: MultiLocationAnalyticsService,
    private siteService: SiteService,
    private statsApiService: StatsApiService,
    private translateService: TranslateService,
    private alertService: SnackbarService,
  ) {
    this.routeParams = route.snapshot.params;
    this.site$ = this.siteService.site$;
    this.site$
      .pipe(
        tap(() => {
          this.loadingDesktopSpeedScore$$.next(true);
        }),
        switchMap((site: Site) => {
          return this.multiLocationAnalyticsService.queryMetrics(
            GetSpeedScoreRequest(site.partnerId, site.accountId, DEVICES.DESKTOP),
          );
        }),
      )
      .subscribe((response) => {
        this.desktopSpeedScoreInfo$$.next(this.parseQueryMetricsResponse(response, DEVICES.DESKTOP));
        this.loadingDesktopSpeedScore$$.next(false);
      });
    this.site$
      .pipe(
        tap(() => {
          this.loadingMobileSpeedScore$$.next(true);
        }),
        switchMap((site: Site) => {
          return this.multiLocationAnalyticsService.queryMetrics(
            GetSpeedScoreRequest(site.partnerId, site.accountId, DEVICES.MOBILE),
          );
        }),
      )
      .subscribe((response) => {
        this.mobileSpeedScoreInfo$$.next(this.parseQueryMetricsResponse(response, DEVICES.MOBILE));
        this.loadingMobileSpeedScore$$.next(false);
      });
  }
  /**
   * Clears the set results cookie.
   */
  clear_cache(): void {
    this.statsApiService.clear_cache();
  }

  getDesktopCachedResults(): SpeedScoreInfo {
    return this.statsApiService.getDesktopCachedResults();
  }

  getMobileCachedResults(): SpeedScoreInfo {
    return this.statsApiService.getMobileCachedResults();
  }

  parseQueryMetricsResponse(response: QueryMetricsResponse, device: string): SpeedScoreInfo {
    const cachedResults = device === DEVICES.DESKTOP ? this.getDesktopCachedResults() : this.getMobileCachedResults();
    let currentScore: number | string = 0;
    let previousScore = 0;
    let updatedDate: Date = new Date();
    let passedRules = 0;
    let opportunities = 0;
    let scoreHistory: ChartSeries[]; // contains one ChartSeries
    let scoreEntry: ChartEntry[]; // once a week score for google pagespeed

    if (!!response && response.metricResults.length) {
      const metrics = response.metricResults[0].metrics.metrics;
      if (!!metrics && metrics.length) {
        const mostRecentMetric = metrics[0].results.metrics[0].measures;
        // use the cached result if it is newer than the result from vstore
        if (!!cachedResults.currentScore && cachedResults.updateDate > metrics[0].dimension) {
          updatedDate = cachedResults.updateDate;
          currentScore = cachedResults.currentScore.value;
          previousScore = Math.round(mostRecentMetric[0] * 100) || 0;
          passedRules = cachedResults.passedRules;
          opportunities = cachedResults.opportunities;
        } else {
          updatedDate = metrics[0].dimension;
          currentScore = Math.round(mostRecentMetric[0] * 100) || 0;
          previousScore = metrics.length > 1 ? metrics[1].results.metrics[0].measures[0] * 100 : 0;
          passedRules = mostRecentMetric[1];
          opportunities = mostRecentMetric[2];
        }
        scoreEntry = metrics.map((m) => {
          return {
            name: alignLocalDateToUTC(m.dimension),
            value: Math.round(m.results.metrics[0].measures[0] * 100),
          };
        });
        scoreHistory = [
          {
            name: 'Score History',
            series: scoreEntry,
          },
        ];
      }
    }

    return {
      device: device,
      currentScore: {
        value: currentScore,
        displayType: 'number',
      },
      previousScore: {
        value: previousScore,
        displayType: 'number',
      },
      updateDate: updatedDate,
      passedRules: passedRules,
      opportunities: opportunities,
      improveCTAKey: this.getImproveSpeedKey(device),
      scoreHistory: scoreHistory && scoreHistory.length > 0 ? scoreHistory : [{ name: '', series: [] }],
    };
  }

  instantFetch(domain: string, device: string, siteId: string): void {
    const siteUrl = 'https://' + domain;
    if (device === DEVICES.DESKTOP) {
      this.desktopRefreshing$$.next(true);
    } else {
      this.mobileRefreshing$$.next(true);
    }
    this.statsApiService.deviceFetch(siteUrl, device).subscribe(
      (result: PageSpeedScoreResult) => {
        const latestRecord =
          device === DEVICES.DESKTOP ? this.desktopSpeedScoreInfo$$.getValue() : this.mobileSpeedScoreInfo$$.getValue();
        const speedScoreInfo = {
          device: device,
          currentScore: {
            value: result.score,
            displayType: 'number',
          } as DisplayValueDescriptor,
          previousScore: latestRecord.currentScore,
          updateDate: result.updateDate,
          passedRules: result.passedRules,
          opportunities: result.opportunities,
          improveCTAKey: this.getImproveSpeedKey(device),
          scoreHistory: latestRecord ? latestRecord.scoreHistory : [{ name: '', series: [] }],
        } as SpeedScoreInfo;
        this.statsApiService.setScore(siteId);
        if (device === DEVICES.DESKTOP) {
          this.desktopSpeedScoreInfo$$.next(speedScoreInfo);
          this.desktopRefreshing$$.next(false);
        } else {
          this.mobileSpeedScoreInfo$$.next(speedScoreInfo);
          this.mobileRefreshing$$.next(false);
        }
      },
      () => {
        const errorMessage = this.translateService.instant('API_ERROR.PAGESPEED_MAX_QUOTA');
        this.alertService.openErrorSnack(errorMessage);
        if (device === DEVICES.DESKTOP) {
          this.desktopRefreshing$$.next(false);
        } else {
          this.mobileRefreshing$$.next(false);
        }
      },
    );
  }

  getImproveSpeedKey(device: string): string {
    return device === DEVICES.DESKTOP
      ? 'ANALYTICS.SPEED_SCORE_MODAL.CTA.IMPROVE_SPEED_DESKTOP'
      : 'ANALYTICS.SPEED_SCORE_MODAL.CTA.IMPROVE_SPEED_MOBILE';
  }
}

export function GetSpeedScoreRequest(partnerId: string, accountId: string, device: string): QueryMetricsRequest {
  const queryMetricRecordsLimit = 5;
  return new QueryMetricsRequest({
    partnerId: partnerId,
    metricName: 'google_pagespeed_insights',
    resourceIds: [
      {
        businessId: { businessId: accountId },
      },
    ],
    measures: [
      {
        aggregate: new Aggregate({
          measure: 'score',
          aggOp: Operator.AVG,
        }),
      },
      {
        aggregate: new Aggregate({
          measure: 'num_of_passed_rules',
          aggOp: Operator.AVG,
        }),
      },
      {
        aggregate: new Aggregate({
          measure: 'num_of_opportunities',
          aggOp: Operator.AVG,
        }),
      },
    ],
    filter: {
      fieldFilter: {
        dimension: 'device',
        operator: FieldFilter_Operator.EQUAL,
        value: {
          valueType: Type.PROPERTY_TYPE_STRING,
          value: device,
        },
      },
    },
    groupBy: new GroupBy({
      dimension: [
        new Dimension({
          dimension: 'collection_datetime',
        }),
        new Dimension({
          dimension: 'device',
        }),
      ],
    }),
    orderBy: new OrderBy({
      orderBy: [
        new OrderColumn({
          column: 'collection_datetime',
          order: Order.ORDER_DESC,
        }),
      ],
    }),
    limit: queryMetricRecordsLimit,
  });
}
